import React, { useEffect, useRef, useState } from "react";
import { db } from "../Firebase";
import { collection, addDoc } from "firebase/firestore";
import {
  FiMapPin,
  FiPhone,
  FiClock,
  FiSend,
  FiAlertCircle,
} from "react-icons/fi";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./ContactPage.css";

gsap.registerPlugin(ScrollTrigger);

const ContactPage = () => {
  const containerRef = useRef(null);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "General Enquiry",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await addDoc(collection(db, "messages"), {
        ...formData,
        createdAt: new Date().toISOString(),
      });

      // Send email using formsubmit.co (table template)
      await fetch("https://formsubmit.co/ajax/swaruphospital154@gmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          _subject: `New Contact Enquiry from ${formData.firstName} ${formData.lastName}`,
          _template: "table",
          "First Name": formData.firstName,
          "Last Name": formData.lastName,
          Email: formData.email,
          Subject: formData.subject,
          Message: formData.message,
          _autoresponse:
            "Thank you for contacting Swarup Hospital. We will get back to you shortly.",
        }),
      });

      setSubmitStatus("success");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        subject: "General Enquiry",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    // Scroll stagger animations for cards
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".contact-card, .emergency-banner",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".contact-info-row",
            start: "top 85%",
            toggleActions: "play none none none",
          },
        },
      );

      gsap.fromTo(
        ".map-container, .message-form-card",
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.15,
          ease: "power2.out",
          scrollTrigger: {
            trigger: ".contact-main-row",
            start: "top 85%",
            toggleActions: "play none none none",
          },
        },
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="contact-page-wrapper" ref={containerRef}>
      <header
        className="contact-header"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(10, 36, 34, 0.52) 0%, rgba(10, 36, 34, 0.62) 100%), url(${process.env.PUBLIC_URL}/assets/images/img16.png)`,
        }}
      >
        <div className="contact-header-overlay" />
        <div className="contact-header-content">
          <h1>Locations & Contact</h1>
          <p>
            Get in touch with Swarup Hospital, Kolhapur for appointments, medical enquiries, emergency assistance and directions to our hospital.
          </p>
        </div>
      </header>

      <div className="contact-container">
        {/* Emergency Banner - Full Width */}
        <div className="emergency-banner">
          <div className="emergency-icon">
            <FiAlertCircle />
          </div>
          <div className="emergency-text">
            <h4>Medical Emergency?</h4>
            <p>
              Call <strong>108</strong> or <strong>+91 9373674641</strong>{" "}
              immediately.
            </p>
          </div>
        </div>

        {/* Contact Info Cards - Aligned in a single straight row */}
        <div className="contact-info-row">
          {/* Address Card */}
          <div className="contact-card">
            <div className="icon-wrapper">
              <FiMapPin />
            </div>
            <h3>Main Hospital</h3>
            <ul className="contact-details-list">
              <li>Swarup Hospital</li>
              <li>154, Azad Galli, Dudhali, Near KMC College,</li>
              <li>Kolhapur, Maharashtra, India.</li>
            </ul>
          </div>

          {/* Contact Details Card */}
          <div className="contact-card">
            <div className="icon-wrapper">
              <FiPhone />
            </div>
            <h3>Get in Touch</h3>
            <ul className="contact-details-list">
              <li>
                <span className="contact-type">General Enquiry:</span>
                <a href="tel:+919373674641">+91 9373674641</a>
              </li>
              <li>
                <span className="contact-type">Appointments:</span>
                <a href="tel:+919373674641">+91 9373674641</a>
              </li>
              <li>
                <span className="contact-type">Email:</span>
                <a href="mailto:care@swaruphospital.com">
                  swaruphospital154@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Hours Card */}
          <div className="contact-card">
            <div className="icon-wrapper">
              <FiClock />
            </div>
            <h3>Opening Hours</h3>
            <ul className="contact-details-list hours-list">
              <li>
                <strong>Emergency:</strong>
                <span className="badge-open">24/7 Open</span>
              </li>
              <li>
                <strong>OPD Timings:</strong>
                <span>Mon - Sat, 9:00 AM - 8:00 PM</span>
              </li>
              <li>
                <strong>Pharmacy:</strong>
                <span className="badge-open">24/7 Open</span>
              </li>
              <li>
                <strong>Visiting Hours:</strong>
                <span>9:00 AM - 7:00 PM</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Form & Map - Aligned side-by-side in a straight row */}
        <div className="contact-main-row">
          <div className="message-form-card">
            <h3 className="form-title">Send us a message</h3>
            <div className="title-underline" />

            {submitStatus === "success" && (
              <div
                style={{
                  background: "#dcfce3",
                  color: "#166534",
                  padding: "15px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                Your message has been sent successfully. We will get back to you
                soon.
              </div>
            )}

            {submitStatus === "error" && (
              <div
                style={{
                  background: "#fee2e2",
                  color: "#991b1b",
                  padding: "15px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                Failed to send message. Please try again later.
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your first name"
                    required
                    value={formData.firstName}
                    onChange={(e) =>
                      setFormData({ ...formData, firstName: e.target.value })
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter your last name"
                    required
                    value={formData.lastName}
                    onChange={(e) =>
                      setFormData({ ...formData, lastName: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="Enter your email address"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>

              <div className="form-group">
                <label className="form-label">Subject</label>
                <div className="select-wrapper">
                  <select
                    className="form-select"
                    value={formData.subject}
                    onChange={(e) =>
                      setFormData({ ...formData, subject: e.target.value })
                    }
                  >
                    <option value="General Enquiry">General Enquiry</option>
                    <option value="Feedback">Feedback</option>
                    <option value="Billing Query">Billing Query</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Message</label>
                <textarea
                  className="form-textarea"
                  placeholder="Write your message here..."
                  rows="4"
                  required
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                ></textarea>
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
              >
                <FiSend style={{ marginRight: "8px" }} />{" "}
                {isSubmitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>

          {/* Map Container */}
          <div className="map-container">
            <iframe
              title="Hospital Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3821.5761030862254!2d74.21511307543375!3d16.69808262229596!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc1aaa00fcfffbf%3A0x705713fa5e4af103!2sSwarup%20Hospital!5e0!3m2!1sen!2sin!4v1765534745951!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
